import React from 'react';
import { Link } from 'react-router-dom';
import { PolicyPage, Section, Bullets, MERCHANT } from './legal';

const DrivelineTerms = () => (
  <PolicyPage
    testid="driveline-terms"
    title="Terms of Service"
    intro="These terms govern your use of Driveline, the workshop management software operated by
           Unisol Global. By creating an account you agree to them."
  >
    <Section title="1. Who we are" testid="terms-parties">
      <p>
        Driveline is a software service owned and operated by <b>{MERCHANT.legal}</b>, {MERCHANT.address}.
        In these terms, "we" and "us" means {MERCHANT.legal}; "you" means the workshop or business that
        holds the account, including everyone you give a login to.
      </p>
    </Section>

    <Section title="2. Your account" testid="terms-account">
      <Bullets items={[
        'You must be a business or an adult authorised to act for one. Driveline is not sold to consumers.',
        'The information you give us at signup must be accurate, and kept up to date — we use it for invoices, GST documents and service notices.',
        'You are responsible for everything done under your logins, including those of your staff. Keep passwords confidential and remove staff access when someone leaves.',
        'Tell us promptly if you believe an account has been accessed without authorisation.',
      ]} />
    </Section>

    <Section title="3. Subscription, trial and payment" testid="terms-billing">
      <p>
        Access is sold as a recurring subscription on the plan you select. The commercial terms —
        prices, taxes, trial length, billing cycle, auto-debit mandates and failed-payment handling —
        are set out on the{' '}
        <Link to="/driveline/pricing" className="text-teal-700 underline">Pricing</Link> page, and
        cancellations and refunds on the{' '}
        <Link to="/driveline/refund-policy" className="text-teal-700 underline">Refund &amp; Cancellation</Link>{' '}
        page. Both pages form part of these terms.
      </p>
      <p>
        If a subscription lapses or a payment fails, your account becomes read-only: your data stays
        intact and downloadable, but new records cannot be created until payment is settled.
      </p>
    </Section>

    <Section title="4. Your data is yours" testid="terms-data">
      <Bullets items={[
        'You own the customer records, vehicle details, photos, invoices and documents you put into Driveline. We do not sell them and we do not use them to market to your customers.',
        'We process that data only to run the service for you, as described in our Privacy Policy.',
        'You can export your data at any time while the account is active or read-only.',
        'You are responsible for having the right to hold the customer information you upload, and for using it lawfully — including obtaining consent where the law requires it.',
      ]} />
    </Section>

    <Section title="5. Acceptable use" testid="terms-acceptable">
      <p>You agree not to:</p>
      <Bullets items={[
        'Use Driveline for anything unlawful, or to store content you have no right to store.',
        'Resell, sublicense or white-label the service to third parties without our written agreement.',
        'Copy, reverse-engineer or attempt to extract the source code of the software.',
        'Probe, scan or attempt to breach the security of the service, or interfere with other workshops\' accounts.',
        'Send spam or bulk unsolicited messages through the service\'s email or WhatsApp sharing features.',
        'Place an unreasonable load on the platform through automated scraping or bulk API abuse.',
      ]} />
      <p>
        We may suspend an account that materially breaches this section. Where it is safe and lawful
        to do so, we will tell you first and give you a chance to fix the problem.
      </p>
    </Section>

    <Section title="6. Availability and support" testid="terms-availability">
      <p>
        We aim to keep Driveline available at all times and work to restore service quickly when it
        is not. We do not promise uninterrupted availability: maintenance, third-party outages
        (hosting, payment gateway, email, WhatsApp) and events outside our control can interrupt it.
        Planned maintenance that needs downtime is notified in advance where possible.
      </p>
      <p>
        Support is by email at{' '}
        <a className="text-teal-700 underline" href={`mailto:${MERCHANT.email}`}>{MERCHANT.email}</a>{' '}
        during {MERCHANT.hours}.
      </p>
    </Section>

    <Section title="7. Third-party services" testid="terms-thirdparty">
      <p>
        Driveline relies on third parties to deliver parts of the service — payment collection
        (Cashfree Payments), email delivery, cloud hosting, file storage and optional AI features.
        Their handling of data is covered in our Privacy Policy. We are not liable for the acts or
        outages of these providers beyond our duty to choose them with reasonable care.
      </p>
    </Section>

    <Section title="8. Not professional advice" testid="terms-advice">
      <p>
        Driveline helps you produce GST invoices, returns data, payroll figures and warranty
        documents, but it is a tool, not a tax, legal or accounting advisor. You remain responsible
        for the accuracy of what you file and issue, and we recommend your accountant reviews it.
      </p>
    </Section>

    <Section title="9. Liability" testid="terms-liability">
      <p>
        To the extent permitted by law, our total liability to you for any claim arising out of or
        relating to the service is limited to the subscription fees you paid us in the 3 months before
        the claim arose. We are not liable for indirect or consequential loss, loss of profit, loss of
        business, or loss of data beyond our obligation to maintain reasonable backups.
      </p>
      <p>
        Nothing in these terms excludes liability that cannot be excluded under Indian law, including
        for fraud or wilful misconduct.
      </p>
    </Section>

    <Section title="10. Termination" testid="terms-termination">
      <p>
        You may cancel at any time as described in the Refund &amp; Cancellation Policy. We may
        terminate or suspend an account for material breach of these terms, for non-payment, or if we
        discontinue the service — in the last case we will give at least 60 days' notice and refund any
        unused prepaid period. On termination you may export your data for 30 days.
      </p>
    </Section>

    <Section title="11. Changes to these terms" testid="terms-changes">
      <p>
        We may update these terms as the product and the law change. Material changes are notified by
        email to the account's registered address, and in the app, at least 14 days before they take
        effect. Continuing to use Driveline after that date means you accept the revised terms.
      </p>
    </Section>

    <Section title="12. Governing law" testid="terms-law">
      <p>
        These terms are governed by the laws of India. The courts at Bangalore, Karnataka have
        exclusive jurisdiction over any dispute, which we will both first attempt to resolve in good
        faith by discussion.
      </p>
    </Section>
  </PolicyPage>
);

export default DrivelineTerms;
